import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { ModalBase } from '@components/Modal';
import ImportLoadingOverlay from '@components/ImportLoadingOverlay';
import { Typography } from '@components/Typography';
import { useTranslation } from '@hooks';
import { alertActions } from '@actions';
import { menusService } from '@services';
import { handleApiError } from '@helpers/api';
import { validateFileSize } from '@helpers/upload-file';
import { MB } from '@constants/file';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * MB;

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        outline: 'none',
        width: 600,
    },
});

const Content = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 16,
});

const DropZone = styled('div', {
    shouldForwardProp: prop => prop !== 'isDragOver',
})(({ isDragOver }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    padding: '32px 16px',
    border: '1px dashed rgba(0, 0, 0, 0.3)',
    borderRadius: 8,
    cursor: 'pointer',
    backgroundColor: isDragOver ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
}));

const FileRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '4px 8px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 8,
});

const formatSize = bytes => `${(bytes / MB).toFixed(1)} MB`;

const ImportModal = ({ isOpen, onClose, onImported }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const inputRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isImporting, setIsImporting] = useState(false);

    const addFiles = newFiles => {
        const accepted = [...files];
        for (const file of newFiles) {
            const { error, metric, size } = validateFileSize(file, MAX_FILE_SIZE);
            if (error) {
                dispatch(alertActions.error(t(`apiErrors.${error}`, { size, metric })));
                continue;
            }
            if (accepted.length >= MAX_FILES) {
                dispatch(alertActions.error(t('MenusPage.importTooManyFiles', { count: MAX_FILES })));
                break;
            }
            accepted.push(file);
        }
        setFiles(accepted);
    };

    const onInputChange = e => {
        addFiles(Array.from(e.target.files));
        e.target.value = '';
    };

    const onDrop = e => {
        e.preventDefault();
        setIsDragOver(false);
        addFiles(Array.from(e.dataTransfer.files));
    };

    const onRemoveFile = index => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const onImport = async () => {
        const formData = new FormData();
        files.forEach(file => formData.append('files', file));

        setIsImporting(true);
        try {
            const draft = await menusService.importMenu(formData);
            setFiles([]);
            onImported(draft);
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsImporting(false);
        }
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                handleClose={onClose}
                closeDisabled={isImporting}
                title={t('MenusPage.importWithAi')}
                onPrimaryAction={onImport}
                primaryActionText={t('MenusPage.importButton')}
                primaryActionDisabled={files.length === 0 || isImporting}
            >
                <Content>
                    <Typography variant="body2" color="textSecondary">
                        {t('MenusPage.importModalDescription')}
                    </Typography>
                    <DropZone
                        isDragOver={isDragOver}
                        onClick={() => inputRef.current?.click()}
                        onDragOver={e => {
                            e.preventDefault();
                            setIsDragOver(true);
                        }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={onDrop}
                    >
                        <UploadFileRoundedIcon color="action" />
                        <Typography variant="body2" color="textPrimary">
                            {t('MenusPage.importDropFiles')}
                        </Typography>
                    </DropZone>
                    <input ref={inputRef} type="file" multiple hidden onChange={onInputChange} />
                    {files.map((file, index) => (
                        <FileRow key={`${file.name}-${index}`}>
                            <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                            <Typography variant="body2" color="textPrimary" sx={{ flex: 1, wordBreak: 'break-all' }}>
                                {file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {formatSize(file.size)}
                            </Typography>
                            <IconButton size="small" onClick={() => onRemoveFile(index)}>
                                <CloseRoundedIcon fontSize="small" />
                            </IconButton>
                        </FileRow>
                    ))}
                </Content>
            </Modal>
            <ImportLoadingOverlay
                open={isImporting}
                messages={[
                    t('MenusPage.importReadingFiles'),
                    t('MenusPage.importExtractingItems'),
                    t('MenusPage.importFetchingImages'),
                    t('MenusPage.importAlmostDone'),
                ]}
            />
        </>
    );
};

export default ImportModal;
